<script setup lang="ts">
import type { NuxtError } from '#app'

const props = defineProps({
  error: {
    type: Object as PropType<NuxtError>,
    required: true
  }
})

useHead({
  htmlAttrs: {
    lang: 'en'
  }
})

const isCharacterNotFound = computed(() =>
  props.error.statusCode === 404 && props.error.statusMessage === 'Character not found'
)

// Get username from error data (passed from [username].vue)
const attemptedUsername = computed(() => {
  if (!isCharacterNotFound.value) return ''
  return (props.error.data as { username?: string })?.username || ''
})

useSeoMeta({
  title: isCharacterNotFound.value ? `${attemptedUsername.value} - Claim this username` : 'Page not found',
  description: isCharacterNotFound.value
    ? `mychar.gg/${attemptedUsername.value} is available. Claim it now!`
    : 'We are sorry but this page could not be found.'
})

const { data: navigation } = await useAsyncData('navigation', () => queryCollectionNavigation('docs'), {
  transform: data => data.find(item => item.path === '/docs')?.children || []
})
const { data: files } = useLazyAsyncData('search', () => queryCollectionSearchSections('docs'), {
  server: false
})

const links = [{
  label: 'Docs',
  icon: 'i-lucide-book',
  to: '/docs/getting-started'
}, {
  label: 'Pricing',
  icon: 'i-lucide-credit-card',
  to: '/pricing'
}, {
  label: 'Blog',
  icon: 'i-lucide-pencil',
  to: '/blog'
}]
</script>

<template>
  <div>
    <AppHeader />

    <UMain>
      <UContainer>
        <!-- Character not found - claim username UI -->
        <div
          v-if="isCharacterNotFound"
          class="flex flex-col items-center justify-center min-h-screen py-24 text-center"
        >
          <div class="flex items-center justify-center size-16 rounded-2xl bg-primary-500/10 mb-6">
            <UIcon
              name="i-lucide-user-plus"
              class="size-8 text-primary-400"
            />
          </div>

          <h1 class="text-3xl sm:text-4xl font-bold text-highlighted mb-3">
            This username is available
          </h1>
          <p class="text-lg text-muted mb-10 max-w-md">
            <span class="text-primary font-medium">mychar.gg/{{ attemptedUsername }}</span> hasn't been claimed yet. Make it yours!
          </p>

          <HeroClaimUsername
            variant="compact"
            :initial-username="attemptedUsername"
          />

          <NuxtLink
            to="/"
            class="mt-8 text-sm text-muted hover:text-highlighted transition-colors"
          >
            &larr; Back to home
          </NuxtLink>
        </div>

        <!-- Generic error -->
        <UPage v-else>
          <UError :error="error" />
        </UPage>
      </UContainer>
    </UMain>

    <AppFooter />

    <ClientOnly>
      <LazyUContentSearch
        :files="files"
        shortcut="meta_k"
        :navigation="navigation"
        :links="links"
        :fuse="{ resultLimit: 42 }"
      />
    </ClientOnly>

    <UToaster />
  </div>
</template>
